import axios from 'axios';

// URL base del backend. En dev: localhost:8000. En prod: lo que defina REACT_APP_API_BASE.
export const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

// timeout: sin esto una request colgada (Sheets lento, Render reiniciando)
// se queda esperando hasta que Cloudflare corta a los ~100 s, y el usuario
// ve una pantalla "cargando" eterna. Con 45 s falla, y el interceptor de
// abajo reintenta.
const API = axios.create({ baseURL: API_BASE, timeout: 45000 });

const dormir = (ms) => new Promise(r => setTimeout(r, ms));
// Errores que valen la pena reintentar: sin respuesta (red / timeout) o el
// backend caído-reiniciando (502/503/504 los pone Render/Cloudflare).
const STATUS_TRANSITORIOS = new Set([502, 503, 504]);

// Interceptor: en cada request adjunta el JWT del usuario si está en localStorage.
// Si el componente ya seteó manualmente un Authorization en config.headers, lo respetamos.
// Esto evita tener que pasar { headers: { Authorization: ... } } en cada llamada y arregla
// el "No autenticado" que aparecía al crear eventos / editar / borrar.
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('gulliver_token');
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de respuesta:
// 1) Si el backend devuelve 401, limpiamos el token roto.
// 2) Si NO hay response (Network Error / timeout / connection reset),
//    reintentamos UNA vez después de 600ms — cubre el hipo típico de
//    Sheets/Render sin que el usuario vea el error.
API.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    if (err?.response?.status === 401) {
      // JWT expiró o el servidor no reconoce el token: limpiamos y
      // mandamos al login. Sin el redirect, el usuario ve "Request failed
      // with status code 401" en modales sin entender qué pasa.
      if (localStorage.getItem('gulliver_token')) {
        localStorage.removeItem('gulliver_token');
      }
      // Evitar loop si YA estamos en /login o si el 401 vino del propio
      // login (ej: token de Google inválido).
      const yaEnLogin = window.location.pathname === '/login';
      const urlDeLogin = /\/auth\/(google|me)$/.test(err?.config?.url || '');
      if (!yaEnLogin && !urlDeLogin) {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
    // Reintentos con backoff para errores transitorios.
    //   GET   → hasta 3 reintentos (0.6 s, 1.2 s, 2.4 s). Cubre el típico
    //           reinicio de Render o un hipo de Sheets sin que el usuario
    //           tenga que "actualizar 3 veces".
    //   otros → 1 reintento y solo si NO hubo respuesta (la request pudo
    //           no haber llegado). Con 502/503 no reintentamos escrituras
    //           para no duplicar registros.
    const cfg = err?.config;
    if (cfg) {
      const status = err?.response?.status;
      const esGet = (cfg.method || 'get').toLowerCase() === 'get';
      const sinRespuesta = !err?.response;
      // Un timeout (ECONNABORTED) NO significa que la request no llegó:
      // el backend puede seguir procesándola. Reintentar una escritura
      // en ese caso crea eventos/interacciones duplicados. Solo se
      // reintentan escrituras cuando de verdad no hubo conexión.
      const esTimeout = err?.code === 'ECONNABORTED';
      const transitorio = sinRespuesta || STATUS_TRANSITORIOS.has(status);
      const maxIntentos = esGet ? 3 : (sinRespuesta && !esTimeout ? 1 : 0);
      cfg.__intentos = (cfg.__intentos || 0);
      if (transitorio && cfg.__intentos < maxIntentos) {
        cfg.__intentos += 1;
        await dormir(600 * Math.pow(2, cfg.__intentos - 1));
        try { return await API.request(cfg); } catch (e2) { return Promise.reject(e2); }
      }
    }
    return Promise.reject(err);
  }
);

export default API;

export const getDashboard      = () => API.get('/dashboard');
export const getProyectos      = () => API.get('/proyectos');
export const getProyecto       = (id) => API.get(`/proyectos/${id}`);
export const getStatsEvento    = (id) => API.get(`/eventos/${id}/stats`);
export const getParticipantesEvento = (id) => API.get(`/eventos/${id}/participantes`);
export const getFichaParticipante      = (id) => API.get(`/participantes/${id}`);
export const getParticipantesProyecto  = (id) => API.get(`/participantes?proyecto_id=${id}`);
export const getTodosParticipantes     = () => API.get('/participantes?incluir_proyectos=true');
